import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, GripVertical, Clock, Check, AlertCircle } from 'lucide-react';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { audioApi, comboApi } from '@/lib/api';
import { AUDIO_COLORS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

const MAX_COMBO_DURATION = 300; // 5 minutes

const ComboCreator = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: select audios, 2: name & settings
  const [mode, setMode] = useState('audios'); // 'audios' | 'reglas'
  const [rules, setRules] = useState({}); // { color: count }
  const [availableAudios, setAvailableAudios] = useState([]);
  const [selectedAudios, setSelectedAudios] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [pauseBetween, setPauseBetween] = useState(2);
  const [isPublic, setIsPublic] = useState(true);
  const [comboColor, setComboColor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Calculate total duration
  const totalDuration = selectedAudios.reduce((sum, a) => sum + a.duration_seconds, 0) 
    + Math.max(0, selectedAudios.length - 1) * pauseBetween;

  const isOverLimit = totalDuration > MAX_COMBO_DURATION;

  // Total de audios pedidos por reglas
  const rulesTotal = Object.values(rules).reduce((sum, c) => sum + c, 0);

  // Temáticas con count > 0: 1 sola = Recopilatorio, varias = Combo
  const activeRuleThemes = Object.entries(rules).filter(([, c]) => c > 0).length;
  const isRecopilatorio = mode === 'reglas' && activeRuleThemes === 1;

  // En modo audios: todos del mismo color (y más de 1) = Recopilatorio (solo aviso)
  const selectedColors = new Set(selectedAudios.map(a => a.color));
  const isAudiosRecopilatorio = mode === 'audios' && selectedAudios.length > 1 && selectedColors.size === 1;

  const setRuleCount = (color, delta) => {
    setRules(prev => {
      const next = Math.min(5, Math.max(0, (prev[color] || 0) + delta));
      const updated = { ...prev };
      if (next === 0) {
        delete updated[color];
      } else {
        updated[color] = next;
      }
      return updated;
    });
  };

  // Load available audios
  useEffect(() => {
    const loadAudios = async () => {
      setLoading(true);
      try {
        const audios = await audioApi.getFeed({ limit: 50 });
        setAvailableAudios(audios);
      } catch (err) {
        console.error('Error loading audios:', err);
        setError('Error cargando audios');
      } finally {
        setLoading(false);
      }
    };
    loadAudios();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addAudio = (audio) => {
    if (selectedAudios.find(a => a.audio_id === audio.audio_id)) return;
    setSelectedAudios([...selectedAudios, audio]);
  };

  const removeAudio = (audioId) => {
    setSelectedAudios(selectedAudios.filter(a => a.audio_id !== audioId));
  };

  const moveAudio = (index, direction) => {
    const newList = [...selectedAudios];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newList.length) return;
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setSelectedAudios(newList);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Escribe un nombre para el combo');
      return;
    }
    if (mode === 'reglas') {
      if (rulesTotal === 0) {
        setError('Elige al menos una temática con cantidad mayor que 0');
        return;
      }
      if (rulesTotal > 20) {
        setError('El total de audios de las reglas no puede superar 20');
        return;
      }
    } else {
      if (selectedAudios.length === 0) {
        setError('Añade al menos un audio');
        return;
      }
      if (isOverLimit) {
        setError('El combo excede los 5 minutos');
        return;
      }
    }

    setCreating(true);
    setError('');

    try {
      const result = await comboApi.create({
        name: name.trim(),
        description: description.trim() || null,
        slug: slug.trim() || null,
        audio_ids: mode === 'reglas' ? [] : selectedAudios.map(a => a.audio_id),
        rules: mode === 'reglas'
          ? Object.entries(rules).filter(([, c]) => c > 0).map(([color, count]) => ({ color, count }))
          : null,
        pause_between_seconds: pauseBetween,
        is_public: isPublic,
        color: comboColor
      });
      
      if (onSuccess) onSuccess(result);
    } catch (err) {
      console.error('Create error:', err);
      setError(err.response?.data?.detail || 'Error creando el combo');
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    return (
      <div className="neumorphic-card p-6 text-center">
        <p className="text-gray-600">Inicia sesión para crear combos</p>
      </div>
    );
  }

  return (
    <div className="neumorphic-card p-4 flex flex-col max-h-[85vh]" data-testid="combo-creator">
      {/* Header - Compacto */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            Crear Combo
            {step === 2 && mode === 'reglas' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isRecopilatorio ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
              }`}>
                {isRecopilatorio ? 'Recopilatorio' : 'Combo'}
              </span>
            )}
          </h2>
          <p className="text-xs text-gray-500">Mini-playlist de hasta 5 minutos</p>
        </div>
        <button
          onClick={onCancel}
          className="neumorphic-button-small p-2"
          data-testid="cancel-combo-btn"
        >
          <X size={20} />
        </button>
      </div>

      {/* Mode toggle: Audios concretos / Reglas automáticas */}
      {step === 1 && (
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setMode('audios')}
            className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
              mode === 'audios' ? 'neumorphic-button-accent' : 'neumorphic-button-small text-gray-500'
            }`}
            data-testid="mode-audios-btn"
          >
            Audios
          </button>
          <button
            onClick={() => setMode('reglas')}
            className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
              mode === 'reglas' ? 'neumorphic-button-accent' : 'neumorphic-button-small text-gray-500'
            }`}
            data-testid="mode-reglas-btn"
          >
            🤖 Reglas
          </button>
        </div>
      )}

      {/* Aviso Recopilatorio: una sola temática (reglas o audios del mismo color) */}
      {step === 1 && (isRecopilatorio || isAudiosRecopilatorio) && (
        <div className="bg-amber-50 text-amber-700 p-2 rounded-lg mb-3 text-xs">
          📚 Recopilatorio: una sola temática. Para un Combo mezcla al menos 2.
        </div>
      )}

      {error && (
        <div className="bg-red-100 text-red-600 p-2 rounded-lg mb-3 text-xs flex items-center gap-2">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Duration indicator - Compacto */}
      <div className={`neumorphic-inset p-2 rounded-xl mb-3 flex items-center justify-between ${
        isOverLimit && mode === 'audios' ? 'bg-red-50' : ''
      }`}>
        {mode === 'reglas' ? (
          <>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-500" />
              <span className="font-medium text-sm text-gray-700">
                {rulesTotal} audios por reproducción
              </span>
            </div>
            <span className="text-xs text-gray-500">
              se rellena solo cada día
            </span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Clock size={16} className={isOverLimit ? 'text-red-500' : 'text-gray-500'} />
              <span className={`font-medium text-sm ${isOverLimit ? 'text-red-500' : 'text-gray-700'}`}>
                {formatTime(totalDuration)} / {formatTime(MAX_COMBO_DURATION)}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {selectedAudios.length} audios
            </span>
          </>
        )}
      </div>

      {/* Step 1: Select audios */}
      {step === 1 && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto min-h-0 space-y-3">
            {/* Reglas automáticas: stepper por temática */}
            {mode === 'reglas' && (
              <div>
                <h3 className="font-medium text-gray-700 mb-1 text-sm">Audios por temática:</h3>
                <p className="text-xs text-gray-400 mb-2">El combo se rellena solo con los audios más frescos de cada día</p>
                <div className="space-y-1.5">
                  {Object.entries(AUDIO_COLORS).map(([colorKey, colorData]) => (
                    <div
                      key={colorKey}
                      className="flex items-center gap-2 p-2 rounded-lg neumorphic-button-small"
                    >
                      <span
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                        style={{ backgroundColor: colorData.hex }}
                      >
                        {colorData.emoji}
                      </span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{colorData.name}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setRuleCount(colorKey, -1)}
                          disabled={!rules[colorKey]}
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-30"
                          data-testid={`rule-minus-${colorKey}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-4 text-center text-sm font-medium text-gray-700">
                          {rules[colorKey] || 0}
                        </span>
                        <button
                          onClick={() => setRuleCount(colorKey, 1)}
                          disabled={(rules[colorKey] || 0) >= 5}
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-30"
                          data-testid={`rule-plus-${colorKey}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mode === 'audios' && (<>
            {/* Selected audios */}
            {selectedAudios.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700 mb-2 text-sm">Tu combo:</h3>
                <div className="space-y-1.5">
                  {selectedAudios.map((audio, index) => (
                    <div 
                      key={audio.audio_id}
                      className="flex items-center gap-1.5 p-1.5 bg-audioyou-accent/10 rounded-lg"
                    >
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveAudio(index, -1)}
                          disabled={index === 0}
                          className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30 text-xs"
                        >
                          <Minus size={10} />
                        </button>
                        <button
                          onClick={() => moveAudio(index, 1)}
                          disabled={index === selectedAudios.length - 1}
                          className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30 text-xs"
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 w-4">{index + 1}</span>
                      <AudioVisualBadge
                        shape={audio.shape}
                        color={audio.color}
                        emoji={audio.emoji}
                        language={audio.language}
                        size="xs"
                      />
                      <span className="text-xs text-gray-700 flex-1 truncate">
                        @{audio.username}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {formatTime(audio.duration_seconds)}
                      </span>
                      <button
                        onClick={() => removeAudio(audio.audio_id)}
                        className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available audios */}
            <div>
              <h3 className="font-medium text-gray-700 mb-2 text-sm">Añadir audios:</h3>
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-audioyou-accent border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="space-y-1.5">
                  {availableAudios.map((audio) => {
                    const isSelected = selectedAudios.find(a => a.audio_id === audio.audio_id);
                    return (
                      <button
                        key={audio.audio_id}
                        onClick={() => addAudio(audio)}
                        disabled={isSelected}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                          isSelected 
                            ? 'bg-gray-100 opacity-50' 
                            : 'neumorphic-button-small hover:shadow-md'
                        }`}
                      >
                        <AudioVisualBadge
                          shape={audio.shape}
                          color={audio.color}
                          emoji={audio.emoji}
                          language={audio.language}
                          size="xs"
                        />
                        <span className="text-xs text-gray-700 flex-1 text-left truncate">
                          @{audio.username}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {formatTime(audio.duration_seconds)}
                        </span>
                        {isSelected ? (
                          <Check size={14} className="text-green-500" />
                        ) : (
                          <Plus size={14} className="text-audioyou-accent" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            </>)}
          </div>

          {/* Sticky Next button */}
          <div className="pt-3 mt-auto border-t border-gray-200 bg-white -mx-4 px-4 -mb-4 pb-4 rounded-b-2xl">
            <button
              onClick={() => setStep(2)}
              disabled={mode === 'reglas' ? rulesTotal === 0 : (selectedAudios.length === 0 || isOverLimit)}
              className="neumorphic-button-accent w-full py-3 disabled:opacity-50"
              data-testid="next-step-combo"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Name & settings */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del combo *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Mi mañana rápida"
              className="w-full px-4 py-3 rounded-xl neumorphic-inset focus:outline-none focus:ring-2 focus:ring-audioyou-accent"
              maxLength={50}
              data-testid="combo-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción (opcional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="¿De qué trata este combo?"
              className="w-full px-4 py-3 rounded-xl neumorphic-inset focus:outline-none focus:ring-2 focus:ring-audioyou-accent resize-none"
              rows={2}
              maxLength={200}
            />
          </div>

          {/* Color del combo (opcional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color del combo (opcional)
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={() => setComboColor(null)}
                className={`px-3 h-10 rounded-full text-xs font-medium transition-all ${
                  comboColor === null
                    ? 'bg-gray-800 text-white ring-2 ring-offset-2 ring-gray-400'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="combo-color-auto"
              >
                Auto
              </button>
              {Object.entries(AUDIO_COLORS).map(([key, color]) => (
                <button
                  key={key}
                  onClick={() => setComboColor(key)}
                  className={`w-10 h-10 rounded-full transition-all ${
                    comboColor === key
                      ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                      : 'opacity-80 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: color.hex }}
                  title={color.name}
                  data-testid={`combo-color-${key}`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Auto: usa el color de la primera regla o del primer audio
            </p>
          </div>

          {/* Slug - URL corta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link corto (opcional)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">/c/</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="mi-combo"
                className="flex-1 px-3 py-2 rounded-lg neumorphic-inset focus:outline-none focus:ring-2 focus:ring-audioyou-accent text-sm"
                maxLength={30}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Solo letras, números y guiones</p>
          </div>

          {/* Pause between */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pausa entre audios: {pauseBetween}s
            </label>
            <input
              type="range"
              min={1}
              max={5}
              value={pauseBetween}
              onChange={(e) => setPauseBetween(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-gray-400 mt-1">
              Tiempo para reaccionar entre cada audio
            </p>
          </div>

          {/* Public toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Combo público</span>
            <button
              onClick={() => setIsPublic(!isPublic)}
              className={`w-12 h-6 rounded-full transition-colors ${
                isPublic ? 'bg-audioyou-accent' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                isPublic ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setStep(1)}
              className="neumorphic-button flex-1 py-3"
            >
              Atrás
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !name.trim()}
              className="neumorphic-button-accent flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="create-combo-submit"
            >
              {creating ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Creando...</span>
                </>
              ) : (
                <>
                  <Check size={20} />
                  <span>{isRecopilatorio ? 'Crear Recopilatorio' : 'Crear Combo'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboCreator;
